import React, {
  useEffect,
  useState
} from "react";

import {
  Box,
  Typography,
  Paper,
  Skeleton
} from "@mui/material";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";


import ReportCard from "../components/reports/ReportCard";


import TicketStatusChart from "../components/reports/TicketStatusChart";
import PriorityChart from "../components/reports/PriorityChart";
import ResolutionTimeChart from "../components/reports/ResolutionTimeChart";
import CategoryReport from "../components/reports/CategoryReport";
import MonthlyTrendChart from "../components/reports/MonthlyTrendChart";
import SLAReport from "../components/reports/SLAReport";
import RecentTickets from "../components/reports/RecentTickets";
import ProjectsOverviewReport from "../components/reports/ProjectsOverviewReport";
import CapacityOverviewReport from "../components/reports/CapacityOverviewReport";
import BoardOverviewReport from "../components/reports/BoardOverviewReport";


import AppSelect from "../ui/AppSelect";
import AppButton from "../ui/AppButton";
import {
  getDashboardSummary
} from "../services/dashboardService";
import {
  getProjectsOverview,
  getCapacityOverview,
  getBoardOverview
} from "../services/reportService";
import { useUserRole } from "../context/UserRoleContext";
import { useDepartments } from "../context/DepartmentsContext";
import { USER_ROLES } from "../auth/roles";




function Reports() {


  const [loading, setLoading] = useState(true);


  const [dashboard, setDashboard] = useState(null);

  const [departmentFilter, setDepartmentFilter] = useState("");
  const [exportData, setExportData] = useState(null);


  const { role, employee, loading: roleLoading } = useUserRole();
  const { departments } = useDepartments();


  useEffect(() => {

    if (roleLoading) {
      return;
    }

    const loadSummary = async () => {

      try {

        const managerId = role === USER_ROLES.MANAGER ? (employee?.employee_id ?? -1) : null;

        const [dashboardResponse, projectsOverview, capacityOverview, boardOverview] = await Promise.all([
          getDashboardSummary(null, managerId, departmentFilter || null),
          getProjectsOverview(departmentFilter || null),
          getCapacityOverview(departmentFilter || null),
          getBoardOverview(departmentFilter || null)
        ]);

        setDashboard(dashboardResponse);
        setExportData({ projectsOverview, capacityOverview, boardOverview });

      }

      catch (error) {

        console.error(
          "Reports Error:",
          error
        );

      }

      finally {

        setLoading(false);

      }

    };


    loadSummary();


  }, [role, employee, roleLoading, departmentFilter]);





  if (loading) {


    return (

      <Box

        sx={{

          bgcolor: "background.default",

          minHeight: "100vh",

          p: 3

        }}

      >

        <Skeleton variant="text" width={220} height={40} />

        <Skeleton variant="text" width={320} height={22} sx={{ mb: 3 }} />

        <Box

          sx={{

            display: "grid",

            gridTemplateColumns: {

              xs: "1fr",

              sm: "repeat(2,1fr)",

              md: "repeat(4,1fr)"

            },

            gap: 2,

            mb: 3

          }}

        >

          {Array.from({ length: 8 }).map((_, index) => (

            <Skeleton

              key={`kpi-skeleton-${index}`}

              variant="rounded"

              height={100}

              sx={{ borderRadius: 3 }}

            />

          ))}

        </Box>

        <Box

          sx={{

            display: "grid",

            gridTemplateColumns: {

              xs: "1fr",

              md: "repeat(3,1fr)"

            },

            gap: 2,

            mb: 3

          }}

        >

          {Array.from({ length: 3 }).map((_, index) => (

            <Skeleton

              key={`chart-skeleton-1-${index}`}

              variant="rounded"

              height={330}

              sx={{ borderRadius: 3 }}

            />

          ))}

        </Box>

        <Box

          sx={{

            display: "grid",

            gridTemplateColumns: {

              xs: "1fr",

              md: "repeat(2,1fr)"

            },

            gap: 2

          }}

        >

          {Array.from({ length: 2 }).map((_, index) => (

            <Skeleton

              key={`chart-skeleton-2-${index}`}

              variant="rounded"

              height={330}

              sx={{ borderRadius: 3 }}

            />

          ))}

        </Box>

      </Box>

    );

  }





  if (!dashboard) {

    return null;

  }





  const summary = dashboard.summary;





  const cards = [


    {
      title: "Total Tickets",
      value: summary.total_tickets,
      icon: "🎫"
    },


    {
      title: "Open Tickets",
      value: summary.open_tickets,
      icon: "🔴"
    },


    {
      title: "Assigned Tickets",
      value: summary.assigned_tickets,
      icon: "📋"
    },


    {
      title: "In Progress",
      value: summary.in_progress_tickets,
      icon: "⌛"
    },


    {
      title: "Resolved",
      value: summary.resolved_tickets,
      icon: "✅"
    },


    {
      title: "Closed",
      value: summary.closed_tickets,
      icon: "✔"
    },


    {
      title: "Critical",
      value: summary.critical_tickets,
      icon: "🚨"
    },


    {
      title: "High Priority",
      value: summary.high_priority_tickets,
      icon: "⚠"
    }


  ];


  const handleExportCsv = () => {

    const rows = [
      ["Metric", "Value"],
      ...cards.map((card) => [card.title, card.value]),
      ...(exportData?.projectsOverview?.by_status || []).map((row) => [`Projects — ${row.name}`, row.value]),
      ...(exportData?.capacityOverview?.distribution || []).map((row) => [`Capacity — ${row.name}`, row.value]),
      ["Employees On Bench", exportData?.capacityOverview?.bench_count || 0],
      ...(exportData?.boardOverview?.by_status || []).map((row) => [`Tasks — ${row.name}`, row.value])
    ];

    const csvContent = rows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reports-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };




  return (


    <Box

      sx={{

        bgcolor:"background.default",

        minHeight:"100vh",

        p:3

      }}

    >




      {/* HEADER */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        mb={1}
      >
        <Box>
          <Typography

            variant="h4"

            fontWeight={600}

            color="text.primary"

          >

            Reports & Analytics

          </Typography>

          <Typography

            sx={{

              fontSize:13,

              color:"text.secondary"

            }}

          >

            Overview of ticket, project, capacity and sprint analytics across the tool

          </Typography>
        </Box>

        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          <Box sx={{ minWidth: 220 }}>
            <AppSelect
              placeholder="All departments"
              isClearable
              options={departments.map((dept) => ({ value: dept.department_id, label: dept.department_name }))}
              value={departmentFilter || null}
              onChange={(value) => setDepartmentFilter(value || "")}
            />
          </Box>

          <AppButton
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={handleExportCsv}
            sx={{ height: "auto" }}
          >
            Export CSV
          </AppButton>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }} />




      {/* KPI CARDS */}


      <Box

        sx={{

          display:"grid",

          gridTemplateColumns:{

            xs:"1fr",

            sm:"repeat(2,1fr)",

            md:"repeat(4,1fr)"

          },

          gap:2,

          mb:3

        }}

      >


        {
          cards.map((card,index)=>(

            <ReportCard

              key={index}

              title={card.title}

              value={card.value}

              icon={card.icon}

            />

          ))
        }


      </Box>








      {/* CHART ROW 1 */}


      <Box

        sx={{

          display:"grid",

          gridTemplateColumns:{

            xs:"1fr",

            md:"repeat(3,1fr)"

          },

          gap:2,

          mb:3

        }}

      >




        <Paper

          sx={{

            height:330,

            p:2,

            borderRadius:3,

            border:"1px solid",

            borderColor:"divider",

            boxShadow:"0 2px 8px rgba(0,0,0,0.04)"

          }}

        >

          <TicketStatusChart departmentId={departmentFilter || null} />

        </Paper>






        <Paper

          sx={{

            height:330,

            p:2,

            borderRadius:3,

            border:"1px solid",

            borderColor:"divider",

            boxShadow:"0 2px 8px rgba(0,0,0,0.04)"

          }}

        >

          <PriorityChart departmentId={departmentFilter || null} />

        </Paper>






        <Paper

          sx={{

            height:330,

            p:2,

            borderRadius:3,

            border:"1px solid",

            borderColor:"divider",

            boxShadow:"0 2px 8px rgba(0,0,0,0.04)"

          }}

        >

          <ResolutionTimeChart departmentId={departmentFilter || null} />

        </Paper>




      </Box>









      {/* CHART ROW 2 */}


      <Box

        sx={{

          display:"grid",

          gridTemplateColumns:{

            xs:"1fr",

            md:"repeat(2,1fr)"

          },

          gap:2,

          mb:3

        }}

      >




        <Paper

          sx={{

            height:330,

            p:2,

            borderRadius:3,

            border:"1px solid",

            borderColor:"divider"

          }}

        >

          <CategoryReport departmentId={departmentFilter || null} />

        </Paper>






        <Paper

          sx={{

            height:330,

            p:2,

            borderRadius:3,

            border:"1px solid",

            borderColor:"divider"

          }}

        >

          <MonthlyTrendChart departmentId={departmentFilter || null} />

        </Paper>




      </Box>




      {/* PROJECTS, CAPACITY & BOARD ANALYTICS */}

      <Box sx={{ mb: 3 }}>
        <ProjectsOverviewReport departmentId={departmentFilter || null} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CapacityOverviewReport departmentId={departmentFilter || null} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <BoardOverviewReport departmentId={departmentFilter || null} />
      </Box>




      {/* SLA */}


      <Box sx={{mb:3}}>

        <SLAReport departmentId={departmentFilter || null} />

      </Box>








      {/* RECENT TICKETS */}


      <Box>

        <RecentTickets departmentId={departmentFilter || null} />

      </Box>




    </Box>


  );


}



export default Reports;